import { dumpVars, isNotNullOrUndefined, isNullOrUndefined } from '@utils/utils.tsx';
import { GlobalTimeslots, OpeningModeEnum } from '@api/ringil-api.v2.tsx';
import {
  dayjsFromApiUtc,
  dayjsFromApiUtcInTz,
  dayjsNowInTz,
  formatDateFromDayjsWithLocale,
} from '@utils/dateWithTz.tsx';
import { Dayjs } from 'dayjs';
import { createLogger } from '@utils/logger';

const log = createLogger('timeslotDateTime');

export const formatTimeslotInterval = (timeslots: GlobalTimeslots) => {
  if (isNullOrUndefined(timeslots)) return <>No slots</>;
  if (isNullOrUndefined(timeslots.dayWithInterval)) return <>{dumpVars(timeslots)}</>;

  const openedDays = timeslots.dayWithInterval.filter(t => t.openingMode !== OpeningModeEnum.CLOSED);
  if (openedDays.length < 1) return <></>;

  log.debug('formatTimeslotInterval - timeslots:', timeslots);

  const startInterval = (isNotNullOrUndefined(timeslots.timezoneId) && isNotNullOrUndefined(openedDays[0].utcDay))
    ? formatDateFromDayjsWithLocale(dayjsFromApiUtc(openedDays[0].utcDay))
    : formatDateFromDayjsWithLocale(dayjsFromApiUtcInTz(openedDays[0].day, 'Europe/Prague'));
  const endInterval = (isNotNullOrUndefined(timeslots.timezoneId) && isNotNullOrUndefined(openedDays[openedDays.length - 1].utcDay))
    ? formatDateFromDayjsWithLocale(dayjsFromApiUtc(openedDays[openedDays.length - 1].utcDay))
    : formatDateFromDayjsWithLocale(dayjsFromApiUtcInTz(openedDays[openedDays.length - 1].day, 'Europe/Prague'));

  return `${startInterval} - ${endInterval}`;
};

export const isSomeOpenTimeslotsExists = (timeslots: GlobalTimeslots) => {
  if (isNullOrUndefined(timeslots)) return false;
  if (isNullOrUndefined(timeslots.dayWithInterval)) return false;

  const openedDays = timeslots.dayWithInterval.filter(t => t.openingMode !== OpeningModeEnum.CLOSED);
  return openedDays.length >= 1;
};

/**
 * Creates a new timeslot starting now in the location's timezone
 * @param timezoneId - The timezone for the location
 * @param durationMinutes - Duration of the timeslot in minutes (default: 30)
 * @returns Object with sinceUtc and tillUtc as dayjs in UTC mode
 *
 * @example
 * const { sinceUtc, tillUtc } = createTimeslotNowInTz('America/New_York', 30);
 * // Returns current time in New York timezone converted to UTC, plus 30 minutes
 */
export const createTimeslotNowInTz = (
  timezoneId: string,
  durationMinutes: number = 30
): { sinceUtc: Dayjs; tillUtc: Dayjs } => {
  if (isNullOrUndefined(timezoneId)) {
    log.error('createTimeslotNowInTz: timezoneId is required');
    return null;
  }

  const nowInTz = dayjsNowInTz(timezoneId);
  const sinceUtc = nowInTz.utc();
  const tillUtc = nowInTz.add(durationMinutes, 'minute').utc();

  log.debug(`createTimeslotNowInTz: Timezone: ${timezoneId}, Duration: ${durationMinutes}min, Since: ${sinceUtc.format()}, Till: ${tillUtc.format()}`);

  return { sinceUtc, tillUtc };
};


